'use client';

import { useState, useRef, useEffect } from 'react';
import { FileText, Download, Upload, Copy, Trash2, Eye, EyeOff } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';

function TextEditorClient() {
  const [content, setContent] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [charCountNoSpaces, setCharCountNoSpaces] = useState(0);
  const [lineCount, setLineCount] = useState(0);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [fontSize, setFontSize] = useState('16');
  const [fontFamily, setFontFamily] = useState('monospace');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    updateStats();
  }, [content]);

  const updateStats = () => {
    const words = content.trim() === '' ? 0 : content.trim().split(/\s+/).length;
    const chars = content.length;
    const charsNoSpaces = content.replace(/\s/g, '').length;
    const lines = content === '' ? 0 : content.split('\n').length;

    setWordCount(words);
    setCharCount(chars);
    setCharCountNoSpaces(charsNoSpaces);
    setLineCount(lines);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setContent(text);
      };
      reader.readAsText(file);
    }
  };

  const downloadFile = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'text-document.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(content);
  };

  const clearText = () => {
    if (window.confirm('Are you sure you want to clear all text?')) {
      setContent('');
    }
  };

  const insertText = (text: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.substring(0, start) + text + content.substring(end);
      setContent(newContent);

      // Restore cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + text.length, start + text.length);
      }, 0);
    }
  };

  const getLineNumbers = () => {
    const lines = content.split('\n');
    return lines.map((_, index) => index + 1).join('\n');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
          <FileText className="h-8 w-8 text-purple-600" />
        </div>
        <ToolPageIntro title="Text Editor">
                Professional text editor with rich features including line numbers, statistics, file operations, and multiple formatting options for all your writing needs.
            </ToolPageIntro>
      </div>

      {/* Toolbar */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          {/* File Operations */}
          <div className="flex gap-2">
            <label className="group relative px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 cursor-pointer flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
              <Upload className="w-4 h-4" />
              <span className="font-medium">Upload</span>
              <input
                type="file"
                accept=".txt,.md,.csv,.json,.xml,.html,.css,.js,.ts"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            <button
              onClick={downloadFile}
              className="px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <Download className="w-4 h-4" />
              <span className="font-medium">Download</span>
            </button>
            <button
              onClick={copyToClipboard}
              className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <Copy className="w-4 h-4" />
              <span className="font-medium">Copy</span>
            </button>
            <button
              onClick={clearText}
              className="px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <Trash2 className="w-4 h-4" />
              <span className="font-medium">Clear</span>
            </button>
          </div>

          {/* Formatting Options */}
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setShowLineNumbers(!showLineNumbers)}
              className={`px-3 py-2.5 rounded-xl flex items-center gap-2 font-medium transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 ${showLineNumbers
                ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
            >
              {showLineNumbers ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="text-sm">Lines</span>
            </button>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Size:</label>
              <select
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="12">12px</option>
                <option value="14">14px</option>
                <option value="16">16px</option>
                <option value="18">18px</option>
                <option value="20">20px</option>
                <option value="24">24px</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Font:</label>
              <select
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="monospace">Monospace</option>
                <option value="sans-serif">Sans Serif</option>
                <option value="serif">Serif</option>
                <option value="cursive">Cursive</option>
              </select>
            </div>
          </div>

          {/* Quick Insert */}
          <div className="flex gap-1">
            <button
              onClick={() => insertText('📝')}
              className="px-3 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 text-lg"
              title="Insert note emoji"
            >
              📝
            </button>
            <button
              onClick={() => insertText('✓')}
              className="px-3 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 text-lg"
              title="Insert checkmark"
            >
              ✓
            </button>
            <button
              onClick={() => insertText('→')}
              className="px-3 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 text-lg"
              title="Insert arrow"
            >
              →
            </button>
            <button
              onClick={() => insertText('•')}
              className="px-3 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 text-lg"
              title="Insert bullet"
            >
              •
            </button>
          </div>
        </div>
      </div>

      {/* Editor and Stats */}
      <div className="space-y-6">
        {/* Statistics Row */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Statistics</h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="group bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-blue-700">Words</div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              </div>
              <div className="text-2xl font-bold text-blue-600">{wordCount}</div>
              <div className="text-xs text-blue-600 mt-1">Total words</div>
            </div>

            <div className="group bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl border border-green-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-green-700">Characters</div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
              <div className="text-2xl font-bold text-green-600">{charCount}</div>
              <div className="text-xs text-green-600 mt-1">Including spaces</div>
            </div>

            <div className="group bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-purple-700">No Spaces</div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              </div>
              <div className="text-2xl font-bold text-purple-600">{charCountNoSpaces}</div>
              <div className="text-xs text-purple-600 mt-1">Excluding spaces</div>
            </div>

            <div className="group bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-orange-700">Lines</div>
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              </div>
              <div className="text-2xl font-bold text-orange-600">{lineCount}</div>
              <div className="text-xs text-orange-600 mt-1">Total lines</div>
            </div>

            <div className="group bg-gradient-to-r from-pink-50 to-pink-100 p-4 rounded-xl border border-pink-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-pink-700">Reading Time</div>
                <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
              </div>
              <div className="text-2xl font-bold text-pink-600">
                {wordCount > 0 ? Math.ceil(wordCount / 200) : 0}
              </div>
              <div className="text-xs text-pink-600 mt-1">Minutes</div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-gray-100 rounded-lg">
            <div className="text-xs text-gray-600 text-center">
              <span className="font-medium">Document Info:</span>
              <span className="ml-2">Font: {fontFamily} • Size: {fontSize}px</span>
            </div>
          </div>
        </div>

        {/* Text Editor */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Document</span>
              </div>
              <div className="text-xs text-gray-500">
                {lineCount} lines • {charCount} characters
              </div>
            </div>
          </div>
          <div className="flex gap-0 bg-white">
            {showLineNumbers && (
              <div className="bg-gradient-to-b from-gray-50 to-gray-100 px-2 py-3 text-gray-500 text-sm font-mono select-none border-r border-gray-300 w-12">
                <textarea
                  value={getLineNumbers()}
                  readOnly
                  className="w-full h-[500px] bg-transparent outline-none resize-none text-right leading-relaxed"
                  style={{ fontSize: `${fontSize}px`, fontFamily: fontFamily }}
                />
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="flex-1 h-[500px] outline-none resize-none p-4 bg-white leading-relaxed focus:bg-blue-50/30 transition-colors"
              style={{ fontSize: `${fontSize}px`, fontFamily: fontFamily }}
              placeholder="Start typing your text here..."
            />
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="mt-8 bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="flex items-center gap-3" style={{ position: 'relative', zIndex: 1 }}>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div style={{ lineHeight: '1.4' }}>
              <div className="font-semibold text-gray-900 text-sm">Rich Text Editing</div>
              <div className="text-xs text-gray-600">Write and edit text seamlessly</div>
            </div>
          </div>

          <div className="flex items-center gap-3" style={{ position: 'relative', zIndex: 1 }}>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Upload className="w-5 h-5 text-green-600" />
            </div>
            <div style={{ lineHeight: '1.4' }}>
              <div className="font-semibold text-gray-900 text-sm">File Upload</div>
              <div className="text-xs text-gray-600">Import text files easily</div>
            </div>
          </div>

          <div className="flex items-center gap-3" style={{ position: 'relative', zIndex: 1 }}>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-purple-600" />
            </div>
            <div style={{ lineHeight: '1.4' }}>
              <div className="font-semibold text-gray-900 text-sm">Export Files</div>
              <div className="text-xs text-gray-600">Download as .txt file</div>
            </div>
          </div>

          <div className="flex items-center gap-3" style={{ position: 'relative', zIndex: 1 }}>
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Eye className="w-5 h-5 text-orange-600" />
            </div>
            <div style={{ lineHeight: '1.4' }}>
              <div className="font-semibold text-gray-900 text-sm">Line Numbers</div>
              <div className="text-xs text-gray-600">Toggle line numbering</div>
            </div>
          </div>

          <div className="flex items-center gap-3" style={{ position: 'relative', zIndex: 1 }}>
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Copy className="w-5 h-5 text-red-600" />
            </div>
            <div style={{ lineHeight: '1.4' }}>
              <div className="font-semibold text-gray-900 text-sm">Copy to Clipboard</div>
              <div className="text-xs text-gray-600">Quick copy functionality</div>
            </div>
          </div>

          <div className="flex items-center gap-3" style={{ position: 'relative', zIndex: 1 }}>
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div style={{ lineHeight: '1.4' }}>
              <div className="font-semibold text-gray-900 text-sm">Live Statistics</div>
              <div className="text-xs text-gray-600">Real-time text analysis</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TextEditorClient;
